import { FUSO_ESCRITORIO } from 'dashboard/helper/fusoEscritorio';
// DESTINO DE LIGAÇÃO na tela (telefonia, etapa E — 25/09/2026). O mesmo
// formato do backend (Telephony::Destino): "ramal:103", "fila:2", "ura:1",
// "horario:1". Tudo que escolhe "para onde vai a ligação" (número da
// operadora, tecla da URA, fila sem resposta, horário aberto/fechado) usa
// estas funções — um leitor só, como no PBX.

export const TIPOS = ['ramal', 'fila', 'ura', 'horario'];

export const ICONE = {
  ramal: 'i-ph-phone-fill',
  fila: 'i-ph-users-three-fill',
  ura: 'i-ph-list-numbers-fill',
  horario: 'i-ph-clock-fill',
  sumiu: 'i-ph-warning-fill',
};

export function partes(destino) {
  const m = /^(ramal|fila|ura|horario):([A-Za-z0-9]+)$/.exec(destino || '');
  return m ? { tipo: m[1], alvo: m[2] } : null;
}

const vazio = { ramais: [], filas: [], uras: [], horarios: [] };

// o registro a que o destino aponta (ou null, se sumiu)
export function alvoDe(destino, ctx = vazio) {
  const p = partes(destino);
  if (!p) return null;
  if (p.tipo === 'ramal') return ctx.ramais.find(r => r.numero === p.alvo);
  const lista = { fila: ctx.filas, ura: ctx.uras, horario: ctx.horarios }[
    p.tipo
  ];
  return (lista || []).find(x => String(x.id) === p.alvo) || null;
}

export function nomeDoRamal(r, t) {
  return r.nome || r.usuario?.nome || t('TELEFONIA.OPERADORA.SEM_NOME');
}

// "Ramal 101 · Recepção", "Fila Equipe", "URA Boas-vindas", "Horário Expediente"
export function rotuloDe(destino, ctx, t) {
  const p = partes(destino);
  if (!p) return t('TELEFONIA.ROTEAMENTO.DESTINO.NENHUM');
  const alvo = alvoDe(destino, ctx);
  if (!alvo) return t('TELEFONIA.ROTEAMENTO.DESTINO.SUMIU');
  if (p.tipo === 'ramal')
    return `${t('TELEFONIA.ROTEAMENTO.DESTINO.RAMAL', { numero: alvo.numero })} · ${nomeDoRamal(alvo, t)}`;
  return t(`TELEFONIA.ROTEAMENTO.DESTINO.${p.tipo.toUpperCase()}`, {
    nome: alvo.nome,
  });
}

// As opções do seletor, agrupadas por tipo. `excluir` tira o próprio registro
// (uma URA não manda para ela mesma); `vazio` põe "Ninguém" no topo.
export function opcoesDeDestino(ctx, t, { excluir = '', comVazio = '' } = {}) {
  const G = 'TELEFONIA.ROTEAMENTO.GRUPO';
  const opcoes = [
    ...ctx.ramais
      .filter(r => r.ativo)
      .map(r => ({
        value: `ramal:${r.numero}`,
        label: `${r.numero} · ${nomeDoRamal(r, t)}`,
        group: t(`${G}.RAMAL`),
      })),
    ...ctx.filas.map(f => ({
      value: `fila:${f.id}`,
      label: t('TELEFONIA.ROTEAMENTO.DESTINO.FILA', { nome: f.nome }),
      group: t(`${G}.FILA`),
    })),
    ...ctx.uras.map(u => ({
      value: `ura:${u.id}`,
      label: t('TELEFONIA.ROTEAMENTO.DESTINO.URA', { nome: u.nome }),
      group: t(`${G}.URA`),
    })),
    ...ctx.horarios.map(h => ({
      value: `horario:${h.id}`,
      label: t('TELEFONIA.ROTEAMENTO.DESTINO.HORARIO', { nome: h.nome }),
      group: t(`${G}.HORARIO`),
    })),
  ].filter(o => o.value !== excluir);
  return comVazio ? [{ value: '', label: comVazio }, ...opcoes] : opcoes;
}

// O caminho da ligação a partir de um destino, como árvore: cada nó tem
// tipo, rótulo e os ramos (horário → aberto/fechado; URA → teclas; fila →
// quem toca e o "ninguém atendeu"). Corta laço e profundidade.
export function caminho(destino, ctx, t, visitados = new Set(), fundo = 0) {
  const p = partes(destino);
  if (!p) return null;
  const alvo = alvoDe(destino, ctx);
  const no = {
    destino,
    tipo: alvo ? p.tipo : 'sumiu',
    rotulo: rotuloDe(destino, ctx, t),
    ramos: [],
  };
  if (!alvo || visitados.has(destino) || fundo > 5) return no;
  const vistos = new Set(visitados).add(destino);
  const ramo = (rotulo, d) => {
    const filho = caminho(d, ctx, t, vistos, fundo + 1);
    if (filho) no.ramos.push({ rotulo, no: filho });
  };
  const R = 'TELEFONIA.ROTEAMENTO';
  if (p.tipo === 'horario') {
    ramo(t(`${R}.ABERTO`), alvo.aberto);
    ramo(t(`${R}.FECHADO`), alvo.fechado);
  } else if (p.tipo === 'ura') {
    Object.keys(alvo.opcoes || {})
      .sort()
      .forEach(tecla => ramo(t(`${R}.TECLA`, { tecla }), alvo.opcoes[tecla]));
    if (alvo.senao) ramo(t(`${R}.SEM_TECLA`), alvo.senao);
  } else if (p.tipo === 'fila') {
    no.membros = alvo.ramais
      .map(id => ctx.ramais.find(r => r.id === id))
      .filter(Boolean)
      .map(r => r.numero);
    if (alvo.senao) ramo(t(`${R}.NINGUEM_ATENDE`), alvo.senao);
  }
  return no;
}

// ── Horário ─────────────────────────────────────────────────────────────────
export const DIAS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Aberto agora? (horário de Brasília, como o PBX decide)
export function abertoAgora(horario, agora = new Date()) {
  // forçado ("chegou cedo" / "fechou antes") vale até a hora marcada
  if (horario.forcado && new Date(horario.forcado.ate) > agora)
    return horario.forcado.estado === 'aberto';
  const partesDaData = new Intl.DateTimeFormat('en-US', {
    timeZone: FUSO_ESCRITORIO,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(agora)
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const dia = partesDaData.weekday.toLowerCase().slice(0, 3);
  const hora = `${partesDaData.hour}:${partesDaData.minute}`;
  const mesDia = `${partesDaData.month}-${partesDaData.day}`;
  if ((horario.feriados || []).some(f => f.slice(5) === mesDia)) return false;
  return (horario.faixas || []).some(
    f => (f.dias || []).includes(dia) && f.de <= hora && hora < f.ate
  );
}
